import React, { useEffect, useRef, useState } from 'react';
import { testSiteControlTarget, restartDeviceConfigTarget, checkDeviceConfigRestartConnection } from './siteControlApi';

import DeviceLogSnapshots from './DeviceLogSnapshots';
import DeviceApkUpdates from './DeviceApkUpdates';
import DeviceAndroidConfig from './DeviceAndroidConfig';
import { deviceName } from './deviceUpdateWorkflow';
import './DeviceConfig.css';

const READINESS_LABELS = {
  relay_available: 'Relay available · check the target connection',
  relay_unavailable: 'No eligible relay online',
  endpoint_stale: 'Saved endpoint needs verification in Pairing',
  identity_unavailable: 'Site ADB identity unavailable',
  identity_changed: 'Site identity changed · pair again',
  authorization_required: 'ADB authorization required · open Pairing',
};
const JOB_LABELS = { queued: 'Queued', running: 'Running', succeeded: 'Succeeded', failed: 'Failed' };
const CONFIG_TABS = [{ id: 'updates', label: 'Update Suitestream App' }, { id: 'android', label: 'Android Config' },
  { id: 'logs', label: 'Log Capture' }];
const targetScope = (target) => JSON.stringify([target.deviceId || target.targetDeviceId, target.authMode, target.identityGeneration, target.endpointRevision]);
const isPending = (job) => ['queued', 'running'].includes(job?.status);

function latestRestart(target, local) {
  const saved = target.restartJob;
  if (!local) return saved;
  if (!saved) return local;
  if (saved.id === local.id && local.recoveryState === 'connection_verified') return local;
  return saved.createdAtEpochMs >= local.createdAtEpochMs ? saved : local;
}

function requestKey() {
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function failureMessage(error) {
  const code = error?.response?.data?.status;
  if (code === 'no_eligible_adb_relay') return 'No eligible relay is currently available. Check the relay connection.';
  if (code === 'adb_target_not_authorized') return 'ADB authorization or the saved endpoint needs attention in Pairing.';
  // Do not render raw relay output or unbounded service error text.
  return 'The target connection could not be verified. Check debugging and any “OK to trust this network” prompt on the TV.';
}

export default function DeviceConfigTab({ siteId, snapshot, device, active = true, onDeviceRenamed }) {
  const [activeTab, setActiveTab] = useState('updates');
  const tabButtons = useRef({});
  const tabId = (id) => `device-config-${siteId}-${id}-tab`;
  const panelId = (id) => `device-config-${siteId}-${id}-panel`;
  const [devices, setDevices] = useState([]);
  const namedSnapshot = { ...snapshot, devices: devices.length ? devices : snapshot?.devices || [] };
  const name = (id) => deviceName(id, snapshot, devices);
  const lastSelectedTarget = useRef(null);
  const observedTarget = snapshot?.targets?.find((target) => Number(target.deviceId) === Number(device?.id));
  const selectedTarget = observedTarget || (!snapshot && Number(lastSelectedTarget.current?.deviceId) === Number(device?.id)
    ? lastSelectedTarget.current : null);
  if (snapshot || observedTarget) lastSelectedTarget.current = observedTarget || null;
  const [jobs, setJobs] = useState({});
  const [restartJobs, setRestartJobs] = useState({});
  const [confirmation, setConfirmation] = useState(null);
  const [restartError, setRestartError] = useState('');
  const [restartSubmitting, setRestartSubmitting] = useState(false);
  const dialog = useRef(null);
  const submitting = useRef(false);
  const pending = useRef(new Map());
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    if (confirmation) dialog.current?.querySelector('button')?.focus();
  }, [confirmation]);

  const checkConnection = (target) => {
    if (!target.readiness.canConfigure || !snapshot || snapshot.expiresAtEpochMs <= Date.now()
        || pending.current.has(target.deviceId)) return;
    const job = { scope: targetScope(target), status: 'queued', startedAt: Date.now() };
    pending.current.set(target.deviceId, job);
    const update = (fields) => {
      if (mounted.current) setJobs((previous) => ({ ...previous, [target.deviceId]: { ...job, ...fields } }));
    };
    update({});
    Promise.resolve().then(async () => {
      if (!mounted.current) return;
      update({ status: 'running' });
      try {
        const restart = latestRestart(target, restartJobs[target.deviceId]);
        const recoveryCheck = restart && targetScope(restart) === targetScope(target)
          && ['request_sent', 'delivery_uncertain'].includes(restart.outcome);
        const result = recoveryCheck
          ? await checkDeviceConfigRestartConnection(siteId, restart.id)
          : await testSiteControlTarget(siteId, target.deviceId, {
            controlMethod: 'adb_remote', action: 'ADB_TEST_CONNECTION',
          });
        if (result?.success !== true || result.status !== 'completed') throw new Error('connection_not_verified');
        if (result.job && mounted.current) setRestartJobs((previous) => ({ ...previous, [target.deviceId]: result.job }));
        update({ status: 'succeeded', finishedAt: Date.now(), relayDeviceId: result.bridgeDeviceId,
          message: 'Target connection verified.' });
      } catch (error) {
        update({ status: 'failed', finishedAt: Date.now(), message: failureMessage(error) });
      } finally {
        pending.current.delete(target.deviceId);
      }
    });
  };

  const restartTarget = async () => {
    if (!confirmation || submitting.current) return;
    const target = snapshot?.targets.find((entry) => entry.deviceId === confirmation.target.deviceId);
    if (!target?.restartAvailable || targetScope(target) !== targetScope(confirmation.target)
        || Number(device?.id) !== Number(confirmation.target.deviceId)
        || snapshot.expiresAtEpochMs <= Date.now()) {
      setRestartError('Device readiness changed. Close this confirmation and check the target again.');
      return;
    }
    submitting.current = true;
    setRestartSubmitting(true);
    setRestartError('');
    try {
      const result = await restartDeviceConfigTarget(siteId, target.deviceId, { confirmed: true,
        requestKey: confirmation.requestKey, expected: { identityGeneration: target.identityGeneration,
          endpointRevision: target.endpointRevision, authMode: target.authMode } });
      if (!result?.success || !result.job) throw new Error('restart_not_recorded');
      if (mounted.current) {
        setRestartJobs((previous) => ({ ...previous, [target.deviceId]: result.job }));
        setConfirmation(null);
      }
    } catch (error) {
      // Keep this confirmation's key on a lost response. Status retries cannot create a second reboot.
      const message = {
        adb_restart_scope_changed: 'Connection details changed. Close this confirmation and check the target again.',
        device_config_target_busy: 'Another restart is active or its delivery is uncertain. Wait for its result before starting another.',
        device_config_jobs_unavailable: 'The restart service is currently unavailable. Check the saved report before trying again.',
        device_config_queue_full: 'The restart queue is full. Try this request again later.',
      }[error?.response?.data?.status];
      if (mounted.current) setRestartError(message || 'The restart response was not received. You can check this same request again; it will not send a second restart.');
    } finally {
      submitting.current = false;
      if (mounted.current) setRestartSubmitting(false);
    }
  };

  const fresh = snapshot?.expiresAtEpochMs > Date.now();
  const stored = selectedTarget && jobs[selectedTarget.deviceId];
  const job = selectedTarget && stored?.scope === targetScope(selectedTarget) ? stored : null;
  const candidateRestart = selectedTarget && latestRestart(selectedTarget, restartJobs[selectedTarget.deviceId]);
  const restart = candidateRestart && targetScope(candidateRestart) === targetScope(selectedTarget) ? candidateRestart : null;
  const restartPending = isPending(restart) || (restart?.blocksRestart && restart.deadlineEpochMs > Date.now());

  return (
    <div className="device-config">
      <h2>Device Config</h2>
      <div className="device-config__tabs" role="tablist" aria-label="Device Config sections">
        {CONFIG_TABS.map(({ id, label }, index) => <button key={id} type="button" role="tab"
          id={tabId(id)} aria-controls={panelId(id)} aria-selected={activeTab === id}
          tabIndex={activeTab === id ? 0 : -1} ref={(element) => { tabButtons.current[id] = element; }}
          onClick={() => setActiveTab(id)} onKeyDown={(event) => {
            const next = { ArrowRight: (index + 1) % CONFIG_TABS.length,
              ArrowLeft: (index + CONFIG_TABS.length - 1) % CONFIG_TABS.length,
              Home: 0, End: CONFIG_TABS.length - 1 }[event.key];
            if (next === undefined) return;
            event.preventDefault();
            const nextId = CONFIG_TABS[next].id;
            setActiveTab(nextId);
            tabButtons.current[nextId]?.focus();
          }}>{label}</button>)}
      </div>
      {confirmation && <div className="device-config__confirmation"><div ref={dialog} role="dialog" aria-modal="true"
        aria-label={`Restart ${confirmation.target.name || 'device'}`} onKeyDown={(event) => {
          if (event.key === 'Escape' && !submitting.current) setConfirmation(null);
          if (event.key === 'Tab') {
            const buttons = Array.from(dialog.current.querySelectorAll('button:not(:disabled)'));
            if (!buttons.length) { event.preventDefault(); return; }
            const index = buttons.indexOf(document.activeElement);
            event.preventDefault(); buttons[(index + (event.shiftKey ? -1 : 1) + buttons.length) % buttons.length].focus();
          }
        }}>
        <h3>Restart {name(confirmation.target.deviceId)}?</h3>
        <p>This interrupts playback and disconnects ADB while the device restarts. Debugging may need to be enabled again afterward.</p>
        {restartError && <p role="alert">{restartError}</p>}
        <button type="button" disabled={restartSubmitting} onClick={restartTarget}>{restartSubmitting ? 'Submitting…' : 'Confirm restart'}</button>
        <button type="button" disabled={restartSubmitting} onClick={() => { if (!submitting.current) setConfirmation(null); }}>Cancel</button>
      </div></div>}
      {/* Keep panels mounted so tab changes preserve selections and in-flight jobs. */}
      <div className="device-config__panel" role="tabpanel" id={panelId('updates')}
        aria-labelledby={tabId('updates')} hidden={activeTab !== 'updates'}>
        <DeviceApkUpdates key={`updates-${siteId}`} siteId={siteId} snapshot={snapshot} onInventory={setDevices} />
      </div>
      <div className="device-config__panel" role="tabpanel" id={panelId('android')}
        aria-labelledby={tabId('android')} hidden={activeTab !== 'android'}>
        <DeviceAndroidConfig key={`android-${siteId}-${device?.id || 'none'}-${selectedTarget ? targetScope(selectedTarget) : ''}`} device={device}
          name={device?.name || name(Number(device?.id))} siteId={siteId} target={selectedTarget}
          active={active && activeTab === 'android'} fresh={snapshot?.expiresAtEpochMs > Date.now()}
          onDeviceRenamed={onDeviceRenamed}
          deviceActions={<>
            <button type="button" disabled={!fresh || !selectedTarget?.readiness.canConfigure || isPending(stored) || isPending(restart)}
              onClick={() => checkConnection(selectedTarget)}>Check this device</button>
            <button type="button" disabled={!fresh || !selectedTarget?.restartAvailable || isPending(stored) || restartPending}
              onClick={() => {
                if (pending.current.has(selectedTarget.deviceId)) return;
                setRestartError('');
                setConfirmation({ target: selectedTarget, requestKey: requestKey() });
              }}>Restart</button>
          </>}
          deviceStatus={selectedTarget ? <>
            <p>{selectedTarget.authMode === 'tls' ? 'Wireless debugging' : 'Legacy ADB'} · {selectedTarget.trustState === 'not_required'
              ? 'Target does not require authentication' : selectedTarget.trustState === 'paired' ? 'Authorized' : 'Not authorized'}</p>
            <p>{READINESS_LABELS[selectedTarget.readiness.state] || 'Readiness unavailable'}</p>
            {selectedTarget.readiness.eligibleRelayCount > 0 && <p>{selectedTarget.readiness.eligibleRelayCount} eligible relay(s)</p>}
            {!selectedTarget.restartAvailable && <p>Restart is unavailable with the current site setup.</p>}
            {restart && <div>
              <p>Restart request: {restart.outcome === 'request_sent' ? 'Sent' : JOB_LABELS[restart.status]}</p>
              <p>Requested {new Date(restart.createdAtEpochMs).toLocaleString()}{restart.relayDeviceId ? ` · via ${name(restart.relayDeviceId)}` : ''}</p>
              {restart.outcome === 'not_sent' && <p>Restart was not sent.</p>}
              {restart.outcome === 'delivery_uncertain' && <p>Delivery is uncertain. The request will not be replayed automatically.</p>}
              {restart.outcome === 'delivery_uncertain' && restartPending && <p>Wait until {new Date(restart.deadlineEpochMs).toLocaleTimeString()} before requesting another restart.</p>}
              {restart.recoveryState === 'reconnect_pending' && <p>Reconnect pending. Use Check this device when it is back.</p>}
              {restart.recoveryState === 'connection_verified' && <p>Connection verified after the request. The restart itself has not been verified.</p>}
            </div>}
            <div>
              {job ? <>
                <p>Connection check: {JOB_LABELS[job.status]}</p>
                {job.message && <p>{job.message}</p>}
                {job.relayDeviceId && <p>Via {name(job.relayDeviceId)}</p>}
                {job.finishedAt && <p>Checked {new Date(job.finishedAt).toLocaleString()}</p>}
              </> : <p>No connection check for this target yet.</p>}
            </div>
          </> : !snapshot && <p>Refreshing device readiness…</p>} />

      </div>
      <div className="device-config__panel" role="tabpanel" id={panelId('logs')}
        aria-labelledby={tabId('logs')} hidden={activeTab !== 'logs'}>
        <DeviceLogSnapshots key={siteId} siteId={siteId} snapshot={namedSnapshot} />
      </div>
    </div>
  );
}
